import { FormEvent, KeyboardEvent, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useParams } from "react-router-dom";

import Input from "../../components/Form/Input/input";
import Label from "../../components/Form/Label";
import { FieldHint, FormGroup } from "../../components/Form/styles";
import { KeepoLoader } from "../../components/KeepoLoader";
import { Box } from "../../components/Layout";
import { PageTitle } from "../../components/PageTitle";
import { StickyBarPage } from "../../components/SaveBar";
import { Box as BarBox, Container as BarRow, StickySpacer } from "../../components/SaveBar/styles";
import { Skeleton } from "../../components/Skeleton";
import { useToast } from "../../components/Toast";
import constants from "../../constants";
import {
  createLink,
  errorCodeOf,
  getLink,
  LINK_ERRORS,
  LinkSuggestion,
  suggestSlug,
  updateLink,
} from "../../services/links";
import { CancelLink, FieldError, FormCard, FormSkeleton, Note, SaveButton, SlugRow, TagsField } from "./styles";

const prefix = "pages.links.form";

const MIN_SLUG = 3;
const MAX_TAGS = 10;
const MAX_TAG = 30;
const MAX_DESTINATION = 2048;
const MAX_SLUG = 50;
const MAX_TITLE = 200;

// "Gerar com IA": two sparkles
const SparkleIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M12 2l1.9 5.6L19.5 9.5l-5.6 1.9L12 17l-1.9-5.6L4.5 9.5l5.6-1.9L12 2zm7 11l.95 2.55L22.5 16.5l-2.55.95L19 20l-.95-2.55L15.5 16.5l2.55-.95L19 13z" />
  </svg>
);

// The shared Input draws the field (label with its count, border); our own <input> inside keeps its attributes
const ignore = () => undefined;

/** A path as it is typed: lower case, no accents, words joined by hyphens (the API checks the rest). */
export const tidySlug = (text: string) =>
  text
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9-]+/g, "-")
    .replace(/-{2,}/g, "-")
    .replace(/^-/, "")
    .slice(0, MAX_SLUG);

/** A path as it is saved: as typed, without the hyphens left hanging at the end (typing one mid-word is fine). */
export const finishSlug = (text: string) => tidySlug(text).replace(/-+$/, "");

type Field = "destination" | "slug";

// The API's errors with no field of their own, each with its own text
const FORM_ERRORS: string[] = [
  LINK_ERRORS.rateLimited,
  LINK_ERRORS.notFound,
  LINK_ERRORS.invalidStatus,
  LINK_ERRORS.cannotSave,
  LINK_ERRORS.invalidInput,
];

/**
 * Creating a Keepo Link (/links/new) or changing one (/links/:id/edit): where it goes, its path on kpo.bio (typed,
 * made with AI or random), its title (else the page's) and tags. The fields as the panel's (label and count on top),
 * each error by its field; Cancelar and Salvar on the bar stuck to the bottom, as in SEO.
 */
const LinkFormPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const toast = useToast();
  const { id } = useParams();
  const editing = !!id;

  const [destination, setDestination] = useState("");
  const [slug, setSlug] = useState("");
  const [savedSlug, setSavedSlug] = useState<string>();
  // The short domain shown before the path: the server's (a link being changed brings its own)
  const [shortDomain, setShortDomain] = useState(constants.shortDomain);
  const [title, setTitle] = useState("");
  const [tags, setTags] = useState<string[]>([]);
  const [tagText, setTagText] = useState("");
  const [loadState, setLoadState] = useState<"loading" | "ready" | "notFound" | "error">(editing ? "loading" : "ready");
  const [errors, setErrors] = useState<Partial<Record<Field | "form", string>>>({});
  const [saving, setSaving] = useState(false);
  const [suggesting, setSuggesting] = useState(false);
  // Pasting a destination (or leaving it) fills the path and the title from the page (no AI: quick): only fields
  // empty or filled that way before, never what was typed; once per destination
  const [autoFilling, setAutoFilling] = useState(false);
  const autoSlug = useRef(false);
  const autoTitle = useRef(false);
  const askedFor = useRef<string>();
  // Every request for a path gets a number; only the newest one may fill the fields (a slow answer that was
  // overtaken, from the paste or from the button, is dropped)
  const requestId = useRef(0);
  const pasted = useRef(false);
  const latest = useRef({ slug, title });
  latest.current = { slug, title };

  useEffect(() => {
    if (!id) return;
    let current = true;
    getLink(id)
      .then(link => {
        if (!current) return;
        setDestination(link.destination);
        setSlug(link.slug);
        setSavedSlug(link.slug);
        try {
          setShortDomain(new URL(link.shortUrl).host);
        } catch {
          // the default stays
        }
        setTitle(link.title ?? "");
        setTags(link.tags);
        setLoadState("ready");
      })
      .catch(error => current && setLoadState(errorCodeOf(error) === "NOT_FOUND" ? "notFound" : "error"));
    return () => {
      current = false;
    };
  }, [id]);

  const addTags = (text: string) => {
    const typed = text
      .split(",")
      .map(tag => tag.trim().toLowerCase().slice(0, MAX_TAG))
      .filter(Boolean);
    if (!typed.length) return tags;
    const next = [...tags, ...typed.filter((tag, index) => !tags.includes(tag) && typed.indexOf(tag) === index)].slice(
      0,
      MAX_TAGS
    );
    setTags(next);
    setTagText("");
    return next;
  };

  const onTagKey = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter" || event.key === ",") {
      event.preventDefault();
      addTags(tagText);
    } else if (event.key === "Backspace" && !tagText && tags.length) {
      setTags(tags.slice(0, -1));
    }
  };

  // An error of the API, by its field
  const showError = (error: unknown, fallback: string) => {
    const code = errorCodeOf(error);
    // the link was deleted (in another tab) while it was being changed: nothing to fix here, back to the list
    if (code === LINK_ERRORS.notFound && editing) {
      toast.error(t(`${prefix}.notFound`));
      navigate("/links");
      return;
    }
    const field: Field | "form" =
      code === LINK_ERRORS.invalidDestination || code === LINK_ERRORS.unsafeDestination
        ? "destination"
        : code === LINK_ERRORS.invalidSlug ||
            code === LINK_ERRORS.slugTaken ||
            code === LINK_ERRORS.slugBlocked ||
            code === LINK_ERRORS.aiRateLimit
          ? "slug"
          : "form";
    // said for the whole form: too many tries in a while, and the API's refusals to save
    const known = code && (field !== "form" || FORM_ERRORS.includes(code));
    setErrors({ [field]: t(`${prefix}.errors.${known ? code : fallback}`) });
  };

  /** Puts a suggestion in the fields it may fill (the path always, when asked with the button). */
  const apply = (suggestion: LinkSuggestion, askedForPath: boolean) => {
    if (askedForPath || !latest.current.slug || autoSlug.current) {
      setSlug(suggestion.slug);
      autoSlug.current = true;
    }
    if (suggestion.title && (!latest.current.title || autoTitle.current)) {
      setTitle(suggestion.title);
      autoTitle.current = true;
    }
  };

  // Quiet: nobody asked, so a failure (the AI busy, an address that isn't one) leaves the form as it is
  const autofill = async (value: string) => {
    const url = value.trim();
    const canFill = !latest.current.slug || autoSlug.current || !latest.current.title || autoTitle.current;
    if (editing || !url.includes(".") || url === askedFor.current || !canFill) return;
    askedFor.current = url;
    const request = ++requestId.current;
    setAutoFilling(true);
    try {
      const suggestion = await suggestSlug(url, { ai: false });
      if (requestId.current === request) apply(suggestion, false);
    } catch {
      // the button is still there to ask
    } finally {
      if (requestId.current === request) setAutoFilling(false);
    }
  };

  const suggest = async () => {
    const url = destination.trim();
    askedFor.current = url;
    const request = ++requestId.current;
    setAutoFilling(false);
    setSuggesting(true);
    setErrors({});
    try {
      const suggestion = await suggestSlug(url);
      if (requestId.current === request) apply(suggestion, true);
    } catch (error) {
      if (requestId.current === request) showError(error, "suggest");
    } finally {
      setSuggesting(false);
    }
  };

  const save = async (event: FormEvent) => {
    event.preventDefault();
    const path = finishSlug(slug);
    setSlug(path);
    // an empty path is fine (a random one is made); a typed one needs its 3 letters
    const missing: Partial<Record<Field, string>> = {
      ...(!destination.trim() && { destination: t(`${prefix}.errors.destinationRequired`) }),
      ...(path && path.length < MIN_SLUG && { slug: t(`${prefix}.errors.slugTooShort`) }),
    };
    if (Object.keys(missing).length) {
      setErrors(missing);
      return;
    }
    // an emptied title is sent empty: the API goes back to the page's title
    const link = { destination: destination.trim(), slug: path, title: title.trim(), tags: addTags(tagText) };
    setSaving(true);
    setErrors({});
    try {
      if (id) {
        await updateLink(id, link);
        navigate("/links");
      } else {
        const created = await createLink(link);
        navigate("/links", { state: { created: created.shortUrl } });
      }
    } catch (error) {
      showError(error, "save");
      setSaving(false);
    }
  };

  const slugNote = errors.slug ? (
    <FieldError id="link-slug-error">{errors.slug}</FieldError>
  ) : editing && savedSlug && slug !== savedSlug ? (
    <FieldHint id="link-slug-hint">{t(`${prefix}.slugChanged`)}</FieldHint>
  ) : (
    !editing && <FieldHint id="link-slug-hint">{t(`${prefix}.slugHint`)}</FieldHint>
  );

  return (
    // one shape while loading, missing or ready; while loading, the shape of the title and of the four fields
    <StickyBarPage as="form" noValidate onSubmit={save}>
      <Box className="npb">
        {loadState === "loading" ? (
          <FormSkeleton role="status" aria-label={t(`${prefix}.loading`)}>
            <Skeleton className="title" />
            <span className="card">
              {[0, 1, 2, 3].map(field => (
                <Skeleton key={field} className="field" />
              ))}
            </span>
          </FormSkeleton>
        ) : (
          <PageTitle>{t(`${prefix}.${editing ? "editTitle" : "createTitle"}`)}</PageTitle>
        )}
        {(loadState === "notFound" || loadState === "error") && (
          <>
            <Note role="alert">{t(`${prefix}.${loadState === "notFound" ? "notFound" : "loadError"}`)}</Note>
            <Link to="/links">{t(`${prefix}.back`)}</Link>
          </>
        )}
        {loadState === "ready" && (
          <FormCard>
            <FormGroup>
              {errors.destination && <FieldError id="link-destination-error">{errors.destination}</FieldError>}
              <Input
                id="link-destination"
                type="text"
                label={t(`${prefix}.destination`)}
                value={destination}
                maxLength={MAX_DESTINATION}
                onChange={ignore}
                input={
                  <input
                    id="link-destination"
                    type="text"
                    inputMode="url"
                    autoComplete="url"
                    maxLength={MAX_DESTINATION}
                    placeholder={t(`${prefix}.destinationPlaceholder`)}
                    value={destination}
                    aria-invalid={!!errors.destination}
                    aria-describedby={errors.destination ? "link-destination-error" : undefined}
                    onPaste={() => (pasted.current = true)}
                    onChange={event => {
                      setDestination(event.target.value);
                      if (pasted.current) {
                        pasted.current = false;
                        autofill(event.target.value);
                      }
                    }}
                    onBlur={event => autofill(event.target.value)}
                  />
                }
              />
            </FormGroup>

            <SlugRow>
              <FormGroup>
                {slugNote}
                <Input
                  id="link-slug"
                  type="text"
                  label={t(`${prefix}.slug`)}
                  prefix={`${shortDomain}/`}
                  value={slug}
                  maxLength={MAX_SLUG}
                  ignoreNoValueIconClass
                  onChange={ignore}
                  input={
                    <>
                      <input
                        id="link-slug"
                        aria-busy={autoFilling}
                        type="text"
                        autoCapitalize="none"
                        autoCorrect="off"
                        spellCheck={false}
                        value={slug}
                        aria-invalid={!!errors.slug}
                        aria-describedby={errors.slug ? "link-slug-error" : slugNote ? "link-slug-hint" : undefined}
                        onChange={event => {
                          autoSlug.current = false;
                          setSlug(tidySlug(event.target.value));
                        }}
                        onBlur={() => setSlug(finishSlug(slug))}
                      />
                      {autoFilling && (
                        <span className="field-loader">
                          <KeepoLoader size={14} color="var(--color-neutral-white)" />
                        </span>
                      )}
                    </>
                  }
                />
              </FormGroup>
              <button type="button" className="ai" disabled={!destination.trim() || suggesting} onClick={suggest}>
                <SparkleIcon />
                {/* both texts in the same room, one shown: the button keeps the longer one's width */}
                <span className="labels">
                  <span className={suggesting ? "off" : undefined} aria-hidden={suggesting}>
                    {t(`${prefix}.suggest`)}
                  </span>
                  <span className={suggesting ? undefined : "off"} aria-hidden={!suggesting}>
                    {t(`${prefix}.suggesting`)}
                  </span>
                </span>
              </button>
            </SlugRow>

            <FormGroup>
              <Input
                id="link-title"
                type="text"
                label={t(`${prefix}.title`)}
                value={title}
                maxLength={MAX_TITLE}
                ignoreNoValueIconClass
                onChange={ignore}
                input={
                  <>
                    <input
                      id="link-title"
                      aria-busy={autoFilling}
                      type="text"
                      maxLength={MAX_TITLE}
                      placeholder={t(`${prefix}.titlePlaceholder`)}
                      value={title}
                      onChange={event => {
                        autoTitle.current = false;
                        setTitle(event.target.value);
                      }}
                    />
                    {autoFilling && (
                      <span className="field-loader">
                        <KeepoLoader size={14} color="var(--color-neutral-white)" />
                      </span>
                    )}
                  </>
                }
              />
            </FormGroup>

            <FormGroup>
              <FieldHint id="link-tags-hint">{t(`${prefix}.tagsHint`)}</FieldHint>
              <TagsField>
                {tags.map(tag => (
                  <span key={tag} className="tag">
                    {tag}
                    <button
                      type="button"
                      aria-label={t(`${prefix}.removeTag`, { tag })}
                      onClick={() => setTags(tags.filter(item => item !== tag))}>
                      ×
                    </button>
                  </span>
                ))}
                <input
                  id="link-tags"
                  type="text"
                  value={tagText}
                  aria-describedby="link-tags-hint"
                  disabled={tags.length >= MAX_TAGS}
                  onChange={event =>
                    // pasted with commas: each one a tag
                    event.target.value.includes(",") ? addTags(event.target.value) : setTagText(event.target.value)
                  }
                  onKeyDown={onTagKey}
                  onBlur={() => addTags(tagText)}
                />
              </TagsField>
              <Label htmlFor="link-tags" text={t(`${prefix}.tags`)} maxLength={MAX_TAGS} usedLength={tags.length} />
            </FormGroup>

            {errors.form && <FieldError role="alert">{errors.form}</FieldError>}
          </FormCard>
        )}
      </Box>

      {/* As SaveBar (sticky), with real buttons: Enter in a field saves too */}
      {loadState === "ready" && (
        <>
          <StickySpacer />
          <BarBox $sticky>
            <BarRow>
              <CancelLink to="/links">{t(`${prefix}.cancel`)}</CancelLink>
              <SaveButton type="submit" disabled={saving} $loading={saving}>
                {t(`${prefix}.${editing ? "save" : "create"}`)}
              </SaveButton>
            </BarRow>
          </BarBox>
        </>
      )}
    </StickyBarPage>
  );
};

export default LinkFormPage;
